import React from 'react';
import { Box, Button, Link, Paper, Stack, Tooltip, Typography } from '@mui/material';

import { EncounterIdentityBadges } from './EncounterIdentityBadges';
import type { EncounterFlowStage, EncounterFlowStageKey, EncounterIdentityItem } from './types';

const stageLabels: Record<EncounterFlowStageKey, { present: string; past: string }> = {
  scheduled: { present: 'Schedule', past: 'Scheduled' },
  supported: { present: 'Support', past: 'Supported' },
  signed: { present: 'Sign', past: 'Signed' },
  submitted: { present: 'Submit', past: 'Submitted' },
  settled: { present: 'Settle', past: 'Settled' },
};

const stateColor = {
  complete: '#3E7F63',
  current: '#382299',
  ready: '#B86B00',
  waiting: '#4A6D8C',
  blocked: '#B42318',
  future: '#D9D9E3',
};

const stateFill = {
  complete: true,
  current: true,
  ready: false,
  waiting: true,
  blocked: true,
  future: true,
};

function SignalDot({ state, size = 8 }: { state: EncounterFlowStage['state']; size?: number }) {
  const color = stateColor[state];

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        boxSizing: 'border-box',
        bgcolor: stateFill[state] ? color : 'transparent',
        border: stateFill[state] ? 'none' : `2px solid ${color}`,
        flexShrink: 0,
      }}
    />
  );
}

function IdentityItem({ item }: { item: EncounterIdentityItem }) {
  const content = (
    <>
      <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
        {item.primary}
      </Typography>
      {item.secondary && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {item.secondary}
        </Typography>
      )}
    </>
  );

  if (!item.href && !item.onClick) {
    return <Box sx={{ minWidth: 0 }}>{content}</Box>;
  }

  return (
    <Link
      href={item.href}
      target={item.target}
      rel={item.rel}
      underline="always"
      onClick={item.onClick}
      sx={{
        color: 'inherit',
        cursor: 'pointer',
        display: 'block',
        minWidth: 0,
        textDecorationColor: 'currentColor',
      }}
    >
      {content}
    </Link>
  );
}

function CompactFlowRail({
  stages,
  activeStageKey,
  onStageSelect,
}: {
  stages: EncounterFlowStage[];
  activeStageKey?: EncounterFlowStageKey;
  onStageSelect?: (stage: EncounterFlowStage) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: 0.75,
      }}
    >
      {stages.map(stage => {
        const isComplete = stage.state === 'complete';
        const label = isComplete ? stageLabels[stage.key].past : stageLabels[stage.key].present;
        const segmentColor = stateColor[stage.state];
        const isActive = stage.key === activeStageKey;

        return (
          <Tooltip key={stage.key} title={`${stage.label}: ${stage.detail ?? label}`}>
            <Button
              variant="text"
              size="small"
              onClick={() => onStageSelect?.(stage)}
              sx={{
                minWidth: 0,
                minHeight: 30,
                px: 0.5,
                textTransform: 'none',
                borderBottom: `3px solid ${segmentColor}`,
                borderRadius: 0,
                color: isActive ? 'text.primary' : 'text.secondary',
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  minWidth: 0,
                  lineHeight: 1,
                }}
              >
                <SignalDot state={stage.state} />
                <Typography variant="caption" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {label}
                </Typography>
              </Box>
            </Button>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export interface EncounterBillingCommandBarProps {
  title: React.ReactNode;
  identity: EncounterIdentityItem[];
  stages: EncounterFlowStage[];
  activeStageKey?: EncounterFlowStageKey;
  status?: React.ReactNode;
  primaryAction?: React.ReactNode;
  onStageSelect?: (stage: EncounterFlowStage) => void;
  onCoverageGapClick?: () => void;
}

export function EncounterBillingCommandBar({
  title,
  identity,
  stages,
  activeStageKey,
  status,
  primaryAction,
  onStageSelect,
  onCoverageGapClick,
}: EncounterBillingCommandBarProps) {
  const identityBadges = identity.flatMap(item => item.badges ?? []);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' } }}
        >
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
            {title}
            <EncounterIdentityBadges
              badges={identityBadges}
              size={16}
              onCoverageGapClick={onCoverageGapClick}
            />
            {status}
          </Stack>
          {primaryAction}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', lg: '1fr 1fr 1.2fr 1fr 1fr' },
            gap: 1,
          }}
        >
          {identity.map(item => (
            <IdentityItem key={item.key} item={item} />
          ))}
        </Box>

        <CompactFlowRail
          stages={stages}
          activeStageKey={activeStageKey}
          onStageSelect={onStageSelect}
        />
      </Stack>
    </Paper>
  );
}
